import { createContext, useContext, useState, useCallback } from "react";
import { CheckCircle, X, AlertTriangle, AlertCircle, Info } from "lucide-react";

const SnackbarContext = createContext();

const animationStyles = `
  @keyframes snackbar-in {
    from { opacity: 0; transform: translateY(-20px); }
    to { opacity: 0.98; transform: translateY(0); }
  }
  @keyframes snackbar-out {
    from { opacity: 0.98; transform: translateY(0); }
    to { opacity: 0; transform: translateY(-20px); }
  }
  .snackbar-animate-in { animation: snackbar-in 0.3s ease-out forwards; }
  .snackbar-animate-out { animation: snackbar-out 0.3s ease-in forwards; }
`;

export function SnackbarProvider({ children }) {
  const [snackbars, setSnackbars] = useState([]);

  const removeSnackbar = useCallback((id) => {
    setSnackbars((prev) =>
      prev.map((s) => (s.id === id ? { ...s, exiting: true } : s)),
    );
    setTimeout(() => {
      setSnackbars((prev) => prev.filter((s) => s.id !== id));
    }, 300);
  }, []);

  const addSnackbar = useCallback(
    (message, options = {}) => {
      const config = typeof options === "string" ? { type: options } : options;
      const { timeout = 3000, onRetry, type = "info" } = config;
      const id = Date.now();

      setSnackbars((prev) => [
        ...prev,
        { id, message, onRetry, type, exiting: false },
      ]);
      if (timeout > 0) setTimeout(() => removeSnackbar(id), timeout);
    },
    [removeSnackbar],
  );

  const getTypeStyles = (type) => {
    switch (type) {
      case "success":
        return {
          icon: <CheckCircle className="text-cyan-400" size={20} />,
          border: "border-cyan-500/30",
          glow: "rgba(0, 255, 255, 0.15)",
        };
      case "warning":
        return {
          icon: <AlertTriangle className="text-amber-400" size={20} />,
          border: "border-amber-500/30",
          glow: "rgba(245, 158, 11, 0.15)",
        };
      case "error":
        return {
          icon: <AlertCircle className="text-red-400" size={20} />,
          border: "border-red-500/30",
          glow: "rgba(239, 68, 68, 0.15)",
        };
      default:
        return {
          icon: <Info className="text-teal-400" size={20} />,
          border: "border-teal-500/30",
          glow: "rgba(20, 184, 166, 0.15)",
        };
    }
  };

  return (
    <SnackbarContext.Provider value={{ addSnackbar }}>
      <style>{animationStyles}</style>
      {children}
      <div
        className="fixed left-1/2 top-4 transform -translate-x-1/2 z-[4000] flex flex-col items-center space-y-3 pointer-events-none"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        {snackbars.map((snackbar) => {
          const { icon, border, glow } = getTypeStyles(snackbar.type);
          return (
            <div
              key={snackbar.id}
              className={`snackbar-animate-${snackbar.exiting ? "out" : "in"} pointer-events-auto w-[90vw] max-w-sm px-6 py-4 rounded-2xl shadow-2xl flex flex-col gap-2 relative border ${border} bg-white/95 text-gray-900 backdrop-blur-xl`}
              style={{ boxShadow: `0 10px 30px ${glow}` }}
            >
              <div className="flex items-center gap-3">
                {icon}
                <p className="flex-1 leading-relaxed font-extralight tracking-wide text-sm whitespace-pre-line">
                  {snackbar.message}
                </p>
                <button
                  onClick={() => removeSnackbar(snackbar.id)}
                  className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-black/10 transition-colors"
                >
                  <X size={18} />
                </button>
              </div>
              {snackbar.onRetry && (
                <button
                  onClick={snackbar.onRetry}
                  className="self-start px-3 py-1 bg-black/10 hover:bg-black/20 rounded-full text-xs font-semibold transition"
                >
                  Retry
                </button>
              )}
            </div>
          );
        })}
      </div>
    </SnackbarContext.Provider>
  );
}

export const useSnackbar = () => useContext(SnackbarContext);
